import { classNames } from "@/utility/jsx";
import { t } from "@lingui/core/macro";
import Logger from "@mm/logging";
import { type ChangeEvent, type DragEvent, Fragment, useEffect, useState } from "react";
import styles from "./style.module.scss";

const logger = Logger.fromFilename(__filename);

export function FileUploader(props: {
  onSubmit: (files: FileList) => void;
  /**
   * An addtional text to show in the drag & drop box.
   */
  header?: string;
  /**
   * A boolean to specify if the files are currently uploading.
   * Shows a loading effect if true.
   */
  isUploading?: boolean;
  allowMultiple?: boolean;
  accept?: string;
  extraHeight?: boolean;
}) {
  const extraHeight = props.extraHeight ?? false;
  const allowMultiple = props.allowMultiple ?? true;
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const isUploading = props.isUploading !== undefined ? props.isUploading : false;

  function getFiles(event: DragEvent<HTMLInputElement> | ChangeEvent<HTMLInputElement>) {
    const files = event.currentTarget.files;
    if (!files) {
      logger.error("File could not be loaded from uploader.");
      return;
    }
    props.onSubmit(files);
  }

  useEffect(() => {
    // Once this components mount, this code disables drop & drag event default behavior
    // so dropping a file in other parts of the page does not accidentally open the file in a different tab.
    const body = document.querySelector("body");
    const fileUploaderInputs = document.querySelectorAll(".allow-drop");
    const supressDragEvent = (e: globalThis.DragEvent) => {
      // Only allow the drag event to propagate if the event is emitted from this component's uploader input element.
      let eventIsFromFileUploader = false;
      for (const input of fileUploaderInputs) {
        if (input === e.target) {
          eventIsFromFileUploader = true;
        }
      }
      if (!eventIsFromFileUploader) {
        e.preventDefault();
      }
    };
    body?.addEventListener("dragover", supressDragEvent);
    body?.addEventListener("drop", supressDragEvent);
    return () => {
      // Clean up the supresser on unmount, so that the drag & drop event propagates as usual.
      body?.removeEventListener("dragover", supressDragEvent);
      body?.removeEventListener("drop", supressDragEvent);
    };
  }, []);

  return (
    <div
      className={classNames(
        styles["file-uploader"],
        isDraggedOver && styles["file-uploader-hover"],
        extraHeight && styles["file-uploader-extra-height"],
      )}
      onDragOver={() => {
        setIsDraggedOver(true);
      }}
      onDragLeave={() => {
        setIsDraggedOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setIsDraggedOver(false);
        const files = e.dataTransfer.files;
        if (!files) {
          logger.error("File could not be dropped.");
          return;
        }
        props.onSubmit(files);
      }}
    >
      <div className={styles["file-uploader-instruction"]}>
        {isUploading ? (
          <mm-loader size="large" />
        ) : (
          <Fragment>
            {props.header && <div>{props.header}</div>}
            <span className={classNames("material-icons", styles["upload-icon"])}>file_upload</span>
            <div>{allowMultiple ? t`Select files or drag here.` : t`Select file or drag here.`}</div>
          </Fragment>
        )}
      </div>
      <input
        onChange={getFiles}
        className="allow-drop"
        type="file"
        multiple={allowMultiple}
        accept={props.accept ?? ""}
      />
    </div>
  );
}
